/** Soft feedback prompt: active dwell time in localStorage. */

export const FEEDBACK_PROMPT_THRESHOLD_MS = import.meta.env.DEV
  ? 30_000
  : 30 * 60 * 1000;

const storageKey = (userId: string) => `bilenia.feedbackPrompt.${userId}`;

export type FeedbackPromptState = {
  accumulatedMs: number;
  /** Last time we credited visible dwell (epoch ms). */
  lastTickAt: number | null;
  /** Set when user dismisses tip or opens feedback from it. */
  dismissedAt: number | null;
};

const emptyState = (): FeedbackPromptState => ({
  accumulatedMs: 0,
  lastTickAt: null,
  dismissedAt: null,
});

export function readFeedbackPromptState(userId: string): FeedbackPromptState {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<FeedbackPromptState>;
    return {
      accumulatedMs:
        typeof parsed.accumulatedMs === "number" && parsed.accumulatedMs >= 0
          ? parsed.accumulatedMs
          : 0,
      lastTickAt:
        typeof parsed.lastTickAt === "number" ? parsed.lastTickAt : null,
      dismissedAt:
        typeof parsed.dismissedAt === "number" ? parsed.dismissedAt : null,
    };
  } catch {
    return emptyState();
  }
}

export function writeFeedbackPromptState(
  userId: string,
  state: FeedbackPromptState,
): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

export function isFeedbackPromptDismissed(userId: string): boolean {
  return readFeedbackPromptState(userId).dismissedAt != null;
}

export function markFeedbackPromptDismissed(userId: string): void {
  const state = readFeedbackPromptState(userId);
  writeFeedbackPromptState(userId, {
    ...state,
    dismissedAt: Date.now(),
  });
}

/**
 * Credit visible dwell since last tick. Caps delta to avoid huge jumps after
 * sleep / backgrounding. Returns updated accumulated ms.
 */
export function creditFeedbackPromptDwell(
  userId: string,
  now = Date.now(),
  maxDeltaMs = import.meta.env.DEV ? 3_000 : 15_000,
): number {
  const state = readFeedbackPromptState(userId);
  if (state.dismissedAt != null) return state.accumulatedMs;

  let delta = 0;
  if (state.lastTickAt != null) {
    delta = Math.max(0, Math.min(now - state.lastTickAt, maxDeltaMs));
  }

  const next: FeedbackPromptState = {
    ...state,
    accumulatedMs: state.accumulatedMs + delta,
    lastTickAt: now,
  };
  writeFeedbackPromptState(userId, next);
  return next.accumulatedMs;
}

/** Call when tab becomes hidden so the next visible tick does not credit background time. */
export function pauseFeedbackPromptDwell(userId: string, now = Date.now()): void {
  const state = readFeedbackPromptState(userId);
  if (state.dismissedAt != null) return;
  writeFeedbackPromptState(userId, { ...state, lastTickAt: now });
}
